import { ITransaction } from '@interfaces/transaction';
import { DealBorderTag, PrivilegeBorderTag } from '../Tag';
import { getDateTime12H, isWithinLastMinutes } from '@helpers/date-time.helper';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@constants/route.constant';
import StatusDot from '@assets/images/status-dot.svg';
import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/state';
import { markTransactionAsOld } from '@/state/slice/transactionsSlice';
import { RECENT_TXN_LIMIT_MIN } from '@constants/transaction.constants';
import { TIME_INTERVAL } from '@constants/date-time.constants';
import { usePOSName } from '@hooks/usePOSLookup';

type TransactionTemplateProps = {
	transaction: ITransaction;
};

export function TransactionTemplate({ transaction }: TransactionTemplateProps) {
	const [showHighlight, setShowHighlight] = useState(false);
	const [showStatusDot, setShowStatusDot] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();
	
	// Get POS name
	const posName = usePOSName(transaction.pos);

	const navigateDetail = (id: string) => {
		const search = location.search || '?tab=payments';
		const previousRoute = location.pathname + search;
		navigate(`/${ROUTES.TRANSACTIONS}/${id}`, { state: { previousRoute } });
	};

	const isNewWithinTwoMinutes = (): boolean => {
		if (transaction.isNew) return isWithinLastMinutes(DateTime.fromISO(transaction.createdAt), RECENT_TXN_LIMIT_MIN);
		return false;
	};

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (transaction?.isNew) {
			setShowHighlight(isNewWithinTwoMinutes());
			setShowStatusDot(isNewWithinTwoMinutes());
			interval = setInterval(() => {
				setShowStatusDot(isNewWithinTwoMinutes());
			}, TIME_INTERVAL.ONE_SECOND);
		}
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (showHighlight) {
			timeout = setTimeout(() => {
				setShowHighlight(false);
			}, TIME_INTERVAL.THREE_SECONDS);
		}
		return () => clearTimeout(timeout);
	}, [showHighlight]);

	return (
		<div className='relative'>
			<div
				onClick={() => {
					dispatch(markTransactionAsOld(transaction._id));
					navigateDetail(transaction._id);
				}}
				className='cursor-pointer'>
				{transaction.isNew && showHighlight && (
					<div data-testid='highlight' className='absolute top-0 -left-6  w-[calc(100%+48px)] h-full bg-[#7AFA5699] animate-fadeInOut'></div>
				)}
				<div className='py-4'>
					<div className='flex gap-2'>
						<div className='grow text-blue-grey-dark text-xl font-semibold truncate flex items-center gap-1'>
							{transaction.isNew && showStatusDot && (
								<div data-testid='status-dot'>
									<StatusDot />
								</div>
							)}
							{transaction._id.substring(transaction._id.length - 6)}
							{transaction.voucher && <DealBorderTag />}
							{transaction.privilege && <PrivilegeBorderTag />}
						</div>
						{transaction.amount > 0 ? (
							<div className='flex-none text-green text-xl font-semibold text-right'>+ {transaction.amount.toFixed(2)}</div>
						) : (
							<div className='flex-none text-blue-grey-darkest text-xl font-semibold text-right'>- {(transaction.amount * -1).toFixed(2)}</div>
						)}
					</div>
					<div className='flex gap-2 leading-[18px]'>
						<div className='flex grow gap-2 text-grey-dark'>
							{getDateTime12H(transaction.createdAt)}
							<span className='px-1'>|</span>
							{posName}
						</div>

						<div className='flex-none text-right text-grey-dark'>{transaction.currency}</div>
					</div>
				</div>
				<hr className='border-blue-grey-lighter' />
			</div>
		</div>
	);
}
